import produce from 'immer'
import { translateOpening } from '@editor/components/WallEdit/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'

type LineSegment = [ImmutableVector3, ImmutableVector3]

export interface Coordinates {
  x: number
  y: number
  z: number
}

export type PreservationMode = 'start' | 'end'

export interface WallFormValues {
  guid: string
  start: Coordinates
  end: Coordinates
  placement: WallPlacement
  lengthAdjustment: number
  preservationMode: PreservationMode
  /** connected walls moved along by "Intelligentes Verschieben", saved together with the wall */
  dependentWalls: ShapeObject[]
  label?: string
}

const toVector = (point: Coordinates): ImmutableVector3 =>
  new ImmutableVector3(point.x, point.y, point.z)

const TRANSLATION_EPSILON = 1e-9

export const getWallFormDefaultValues = (wall: ShapeObject, isDraft: boolean): WallFormValues => {
  const [start, end] = wall.shape.points

  // plain copies: react-hook-form keeps class instances (ImmutableVector3) by reference and writes
  // field changes into them, which would otherwise change the wall in the model store
  return {
    guid: wall.guid,
    start: { x: start.x, y: start.y, z: start.z },
    end: { x: end.x, y: end.y, z: end.z },
    placement: wall.placement ?? 'Internal',
    lengthAdjustment: 0,
    preservationMode: 'start',
    dependentWalls: [],
    ...(isDraft ? { label: '' } : {}),
  }
}

export const buildPreviewWallShapePoints = (
  originalPoints: Point[],
  start: ImmutableVector3,
  end: ImmutableVector3,
): ImmutableVector3[] => {
  if (originalPoints.length < 2) return []

  const points = originalPoints.map(toVector)
  const last = points.length - 1

  points[0] = new ImmutableVector3(start.x, start.y, start.z)
  points[1] = new ImmutableVector3(end.x, end.y, end.z)

  if (last === 2) {
    points[2] = new ImmutableVector3(start.x, start.y, points[2].z)
  } else if (points.length > 2) {
    points[2] = new ImmutableVector3(end.x, end.y, points[2].z)
    points[last] = new ImmutableVector3(start.x, start.y, points[last].z)
  }

  return points
}

/**
 * The wall as the form currently describes it: the saved wall with the form's start, end and
 * placement. When both ends moved by the same offset (a translation), the openings move along.
 */
export const buildFormWall = (
  wall: ShapeObject,
  start: Coordinates,
  end: Coordinates,
  placement: WallPlacement,
): ShapeObject => {
  const startVector = toVector(start)
  const endVector = toVector(end)
  const previewPoints = buildPreviewWallShapePoints(wall.shape.points, startVector, endVector)

  const startOffset = startVector.sub(toVector(wall.shape.points[0]))
  const endOffset = endVector.sub(toVector(wall.shape.points[1]))
  const isTranslation =
    startOffset.length() > TRANSLATION_EPSILON &&
    startOffset.distanceTo(endOffset) < TRANSLATION_EPSILON

  return produce(wall, draft => {
    previewPoints.forEach((point, index) => {
      draft.shape.points[index] = point
    })
    draft.placement = placement
    draft.element_type = (placement === 'Internal' ? 'inner_walls' : 'outer_walls') as ElementTypes

    if (isTranslation && draft.openings) {
      draft.openings = wall.openings?.map(opening =>
        translateOpening(opening, startOffset.normalize(), startOffset.length()),
      )
    }
  })
}

export const toNewWallPosition = (wall: ShapeObject): NewWallPosition => {
  const [start, end] = wall.shape.points

  return {
    new_start: { x: start.x, y: start.y },
    new_end: { x: end.x, y: end.y },
    placement: wall.placement || 'External',
  }
}

/**
 * What a wall update does, as the backend's wall update classifies it (`update_wall` with
 * `checkHasNoUpdates`, `checkHasOnlyStartAndEndLengthChangeWithoutFlip` and
 * `checkHasOnlyOrthogonalTranslation` in `modelRouterUtils.py`). One save can either change the
 * length or translate the wall, never both: everything else is `invalid` and rejected.
 */
export type WallUpdateKind = 'none' | 'length' | 'translation' | 'invalid'

export interface WallUpdate {
  kind: WallUpdateKind
  startChanged: boolean
  endChanged: boolean
}

export const NO_WALL_UPDATE: WallUpdate = { kind: 'none', startChanged: false, endChanged: false }

// the backend rounds the new start and end to cm (half up, away from zero) before classifying
const MODEL_QUANTIZATION = 100
const quantize = (value: number) =>
  (Math.sign(value) * Math.round(Math.abs(value) * MODEL_QUANTIZATION)) / MODEL_QUANTIZATION

// tolerance of the backend's collinear/parallel checks for diagonal walls
const DIAGONAL_TOLERANCE = 1e-3
const DIAGONAL_DELTA_DECIMALS = 4

type XY = { x: number; y: number }

const quantizeXY = ({ x, y }: XY): XY => ({ x: quantize(x), y: quantize(y) })
const sameXY = (a: XY, b: XY) => a.x === b.x && a.y === b.y

/** distance of `point` from the infinite line through `start` and `end` */
const distanceToLine = (start: XY, end: XY, point: XY) => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  return Math.abs(dx * (point.y - start.y) - dy * (point.x - start.x)) / Math.hypot(dx, dy)
}

const classifyCartesianUpdate = (
  saved: { start: XY; end: XY },
  start: XY,
  end: XY,
  startChanged: boolean,
  endChanged: boolean,
): WallUpdateKind => {
  // `along` is the coordinate along the wall axis, `across` the one orthogonal to it
  const isXWall = saved.start.y === saved.end.y
  const along = (point: XY) => (isXWall ? point.x : point.y)
  const across = (point: XY) => (isXWall ? point.y : point.x)

  // the new axis must stay cartesian in the same direction
  if (across(start) !== across(end)) return 'invalid'

  const keepsAxisLine = across(start) === across(saved.start) && across(end) === across(saved.end)
  const keepsOrientation = along(saved.start) < along(saved.end) === along(start) < along(end)
  if (keepsAxisLine) return keepsOrientation ? 'length' : 'invalid'

  const keepsAlong = along(start) === along(saved.start) && along(end) === along(saved.end)
  if (keepsAlong && (startChanged || endChanged)) return 'translation'

  return 'invalid'
}

const classifyDiagonalUpdate = (
  saved: { start: XY; end: XY },
  start: XY,
  end: XY,
  startChanged: boolean,
  endChanged: boolean,
): WallUpdateKind => {
  const isCollinear =
    distanceToLine(saved.start, saved.end, start) <= DIAGONAL_TOLERANCE &&
    distanceToLine(saved.start, saved.end, end) <= DIAGONAL_TOLERANCE
  // the backend keeps one point exactly during a length change of a diagonal wall
  if (isCollinear && startChanged !== endChanged) return 'length'

  const factor = 10 ** DIAGONAL_DELTA_DECIMALS
  const sameDelta =
    Math.round((start.x - saved.start.x) * factor) === Math.round((end.x - saved.end.x) * factor) &&
    Math.round((start.y - saved.start.y) * factor) === Math.round((end.y - saved.end.y) * factor)
  if (startChanged && endChanged && sameDelta) return 'translation'

  return 'invalid'
}

export const classifyWallUpdate = (
  saved: { start: XY; end: XY },
  newStart: XY,
  newEnd: XY,
): WallUpdate => {
  const savedXY = { start: quantizeXY(saved.start), end: quantizeXY(saved.end) }
  const start = quantizeXY(newStart)
  const end = quantizeXY(newEnd)

  const startChanged = !sameXY(start, savedXY.start)
  const endChanged = !sameXY(end, savedXY.end)
  if (!startChanged && !endChanged) return NO_WALL_UPDATE

  const isCartesian = savedXY.start.x === savedXY.end.x || savedXY.start.y === savedXY.end.y
  const kind = isCartesian
    ? classifyCartesianUpdate(savedXY, start, end, startChanged, endChanged)
    : classifyDiagonalUpdate(savedXY, start, end, startChanged, endChanged)

  return { kind, startChanged, endChanged }
}

export const INVALID_WALL_UPDATE_MESSAGE =
  'Eine Wand kann pro Speichern entweder verlängert/verkürzt oder senkrecht zu ihrer Achse verschoben werden, nicht beides. Bitte die Koordinaten anpassen oder zurücksetzen.'

export const buildWallShapeOutlineLines = (
  points: ImmutableVector3[],
): {
  outlineLines: LineSegment[]
  startEdgeLine: LineSegment | null
  endEdgeLine: LineSegment | null
} => {
  if (points.length < 2) {
    return { outlineLines: [], startEdgeLine: null, endEdgeLine: null }
  }

  const lastIndex = points.length - 1
  const outlineLines: LineSegment[] = []

  for (let i = 0; i < lastIndex; i++) {
    if (i === 1) continue
    outlineLines.push([points[i], points[i + 1]])
  }

  return {
    outlineLines,
    endEdgeLine: points.length > 2 ? [points[1], points[2]] : null,
    startEdgeLine: points.length > 2 ? [points[0], points[lastIndex]] : null,
  }
}
